import { NAVIGATION_URLS } from '@libs/core';

import { clearCompleteProfileFlowCompletionFlags } from './complete-profile-flow-session';
import type { ProfileCompletionReturnTo } from './constants';
import {
    COMPLETE_PROFILE_ROUTE,
    PROFILE_COMPLETE_CELEBRATION_DISMISSED_KEY,
    PROFILE_COMPLETE_CELEBRATION_HANDOFF_KEY,
    PROFILE_COMPLETE_CELEBRATION_KEY,
    PROFILE_COMPLETE_CELEBRATION_LANDING_KEY,
    PROFILE_COMPLETE_CELEBRATION_LEAVE_PENDING_KEY,
    PROFILE_COMPLETE_FLOW_ORIGIN_KEY,
} from './constants';
import { safeGet, safeRemove, safeSet } from './safe-session-storage';

const RETURN_PATH: Record<ProfileCompletionReturnTo, string> = {
    treats: NAVIGATION_URLS.velocity.account.treatOffers,
    'about-you': NAVIGATION_URLS.velocity.account.aboutYou,
};

const BANNER_PATHS = [NAVIGATION_URLS.velocity.account.aboutYou, NAVIGATION_URLS.velocity.account.treatOffers];

export function normalizeCelebrationPathname(pathname: string): string {
    let end = pathname.length;
    while (end > 0 && pathname[end - 1] === '/') {
        end -= 1;
    }
    return pathname.slice(0, end) || '/';
}

export function setProfileCompleteCelebration(): void {
    safeSet(PROFILE_COMPLETE_CELEBRATION_KEY, 'true');
    markProfileCompleteCelebrationHandoff();
}

export function persistProfileCompleteCelebration(): void {
    safeSet(PROFILE_COMPLETE_CELEBRATION_KEY, 'true');
}

export function clearProfileCompleteCelebration(): void {
    safeRemove(PROFILE_COMPLETE_CELEBRATION_KEY);
}

export function hasProfileCompleteCelebration(): boolean {
    return safeGet(PROFILE_COMPLETE_CELEBRATION_KEY) === 'true';
}

export function markProfileCompleteCelebrationLeavePending(): void {
    safeSet(PROFILE_COMPLETE_CELEBRATION_LEAVE_PENDING_KEY, 'true');
}

export function clearProfileCompleteCelebrationLeavePending(): void {
    safeRemove(PROFILE_COMPLETE_CELEBRATION_LEAVE_PENDING_KEY);
}

export function hasProfileCompleteCelebrationLeavePending(): boolean {
    return safeGet(PROFILE_COMPLETE_CELEBRATION_LEAVE_PENDING_KEY) === 'true';
}

export function markProfileCompleteCelebrationHandoff(): void {
    safeSet(PROFILE_COMPLETE_CELEBRATION_HANDOFF_KEY, 'true');
}

export function clearProfileCompleteCelebrationHandoff(): void {
    safeRemove(PROFILE_COMPLETE_CELEBRATION_HANDOFF_KEY);
}

export function hasProfileCompleteCelebrationHandoff(): boolean {
    return safeGet(PROFILE_COMPLETE_CELEBRATION_HANDOFF_KEY) === 'true';
}

export function setProfileCompleteCelebrationLandingPath(pathname: string): void {
    safeSet(PROFILE_COMPLETE_CELEBRATION_LANDING_KEY, normalizeCelebrationPathname(pathname));
}

export function getProfileCompleteCelebrationLandingPath(): string | null {
    const value = safeGet(PROFILE_COMPLETE_CELEBRATION_LANDING_KEY);
    return value ? normalizeCelebrationPathname(value) : null;
}

export function clearProfileCompleteCelebrationLandingPath(): void {
    safeRemove(PROFILE_COMPLETE_CELEBRATION_LANDING_KEY);
}

export function isCompleteProfilePath(pathname: string): boolean {
    return normalizeCelebrationPathname(pathname) === COMPLETE_PROFILE_ROUTE;
}

export function isProfileCompleteBannerPath(pathname: string): boolean {
    const current = normalizeCelebrationPathname(pathname);
    return BANNER_PATHS.some(path => current === normalizeCelebrationPathname(path));
}

export function isCelebrationLandingPath(pathname: string): boolean {
    const current = normalizeCelebrationPathname(pathname);
    const acceptedLanding = getProfileCompleteCelebrationLandingPath();
    if (acceptedLanding) {
        return current === acceptedLanding;
    }

    if (isProfileCompleteBannerPath(current)) {
        return true;
    }

    return current === normalizeCelebrationPathname(getCompleteProfileReturnPath());
}

export function acceptProfileCompleteCelebrationLanding(pathname: string): void {
    if (isProfileCompleteBannerPath(pathname)) {
        setProfileCompleteCelebrationLandingPath(pathname);
    }
    clearProfileCompleteCelebrationHandoff();
    clearProfileCompleteCelebrationLeavePending();
}

export function markProfileCompleteCelebrationDismissed(userScope?: string | null): void {
    safeSet(PROFILE_COMPLETE_CELEBRATION_DISMISSED_KEY, userScope || 'true');
    clearProfileCompleteCelebration();
    clearProfileCompleteFlowOrigin();
    clearProfileCompleteCelebrationLeavePending();
    clearProfileCompleteCelebrationHandoff();
    clearProfileCompleteCelebrationLandingPath();
    clearCompleteProfileFlowCompletionFlags();
}

export function clearProfileCompleteCelebrationDismissed(): void {
    safeRemove(PROFILE_COMPLETE_CELEBRATION_DISMISSED_KEY);
}

export function hasProfileCompleteCelebrationDismissed(userScope?: string | null): boolean {
    const value = safeGet(PROFILE_COMPLETE_CELEBRATION_DISMISSED_KEY);
    if (!value) {
        return false;
    }

    if (userScope) {
        return value === userScope;
    }

    return value === 'true';
}

export function setProfileCompleteFlowOrigin(returnTo: ProfileCompletionReturnTo): void {
    safeSet(PROFILE_COMPLETE_FLOW_ORIGIN_KEY, returnTo);
}

export function getProfileCompleteFlowOrigin(): ProfileCompletionReturnTo | null {
    const value = safeGet(PROFILE_COMPLETE_FLOW_ORIGIN_KEY);
    if (value === 'treats' || value === 'about-you') {
        return value;
    }
    return null;
}

export function getCompleteProfileReturnPath(): string {
    const origin = getProfileCompleteFlowOrigin();
    if (origin === 'treats' || origin === 'about-you') {
        return RETURN_PATH[origin];
    }
    return RETURN_PATH['about-you'];
}

export function hasProfileCompleteFlowOrigin(): boolean {
    return Boolean(safeGet(PROFILE_COMPLETE_FLOW_ORIGIN_KEY));
}

export function clearProfileCompleteFlowOrigin(): void {
    safeRemove(PROFILE_COMPLETE_FLOW_ORIGIN_KEY);
}

export function destroyProfileCompleteCelebration(userScope?: string | null): void {
    if (!hasProfileCompleteCelebration()) {
        return;
    }
    markProfileCompleteCelebrationDismissed(userScope);
}

export function resetProfileCompleteCelebrationState(): void {
    clearProfileCompleteCelebration();
    clearProfileCompleteFlowOrigin();
    clearProfileCompleteCelebrationLeavePending();
    clearProfileCompleteCelebrationHandoff();
    clearProfileCompleteCelebrationLandingPath();
}

export function resetProfileCompleteCelebrationForAuthEntry(): void {
    resetProfileCompleteCelebrationState();
    clearProfileCompleteCelebrationDismissed();
}
